/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from'react';
import {
  Alert as ShadcnAlert,
  AlertTitle,
  AlertDescription,
} from'@/shared/ui/components/shadcn/alert';
import { cn } from'@/shared/ui/utils/tailwind';

export type AlertProps = {
  children: React.ReactNode;
  title?: string;
  color?:'blue' |'green' |'yellow' |'red';
  icon?: React.ReactNode;
  className?: string;
  onClose?: () => void;
};

const colorClasses: Record<string, string> = {
  blue:'bg-[var(--status-info-bg)] border-[var(--status-info)] text-[var(--status-info)]',
  green:'bg-[var(--status-success-bg)] border-[var(--status-success-border)] text-[var(--status-success-text)]',
  yellow:'bg-[var(--status-warning-bg)] border-[var(--status-warning-border)] text-[var(--status-warning-text)]',
  red:'bg-[var(--status-error-bg)] border-[var(--status-error-border)] text-[var(--status-error-text)]',
};

/**
 * Alert adapter that wraps shadcn Alert with a color-based API.
 *
 * @param {AlertProps} props - Alert properties.
 * @returns {JSX.Element} Rendered alert.
 */
export const Alert: React.FC<AlertProps> = ({
  children,
  title,
  color ='blue',
  icon,
  className,
  onClose,
}) => {
  return (
    <ShadcnAlert className={cn('relative', colorClasses[color], className)}>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 text-current opacity-50 hover:opacity-100"
        >
          ×
        </button>
      )}
      <div className="flex items-start gap-3">
        {icon && <div className="flex-shrink-0">{icon}</div>}
        <div className="flex-1">
          {title && <AlertTitle>{title}</AlertTitle>}
          <AlertDescription>{children}</AlertDescription>
        </div>
      </div>
    </ShadcnAlert>
  );
};

export default Alert;
